import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { get } from "../../Services/ApiService";
import { ServerEndpoint } from "../../Redux/endpoints.ts";
import MediaComponent from "../../Utils/Media/media-component";
import SystemDataService from "../../Services/SystemDataService";
import { getPaymentSuccessData } from "../../Public/payment-success-data.js";

const DELIVERY_STEPS = ['paid', 'received', 'processing', 'ready_for_shipping', 'shipped', 'delivered', 'done'];
const PICKUP_STEPS   = ['paid', 'received', 'processing', 'ready_for_pickup', 'picked_up', 'done'];

const OrderTracker = () => {
    const location = useLocation();
    const { links = [], language = 'en' } = useSelector((state) => state.systemData || {});
    const txt  = useMemo(() => getPaymentSuccessData(language), [language]);
    const logo = SystemDataService.byLabel(links, 'Logo Full');

    const deliveryRegistry = useMemo(() => [
        { key: 'paid',               asset: SystemDataService.byLabel(links, 'Status Paid Icon'),       fallbackLabel: txt.statuses.paid.fallbackLabel },
        { key: 'received',           asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.received.fallbackLabel },
        { key: 'processing',         asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.processing.fallbackLabel },
        { key: 'ready_for_shipping', asset: SystemDataService.byLabel(links, 'Status Shipped Icon'),    fallbackLabel: txt.statuses.ready_for_shipping.fallbackLabel },
        { key: 'shipped',            asset: SystemDataService.byLabel(links, 'Status Shipped Icon'),    fallbackLabel: txt.statuses.shipped.fallbackLabel },
        { key: 'delivered',          asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.delivered.fallbackLabel },
        { key: 'done',               asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.done.fallbackLabel },
    ], [links, txt]);

    const pickupRegistry = useMemo(() => [
        { key: 'paid',             asset: SystemDataService.byLabel(links, 'Status Paid Icon'),       fallbackLabel: txt.statuses.paid.fallbackLabel },
        { key: 'received',         asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.received.fallbackLabel },
        { key: 'processing',       asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.processing.fallbackLabel },
        { key: 'ready_for_pickup', asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.ready_for_pickup.fallbackLabel },
        { key: 'picked_up',        asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.picked_up.fallbackLabel },
        { key: 'done',             asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.done.fallbackLabel },
    ], [links, txt]);

    const [order,   setOrder]   = useState(null);
    const [loading, setLoading] = useState(true);

    const orderId = new URLSearchParams(location.search).get('id');

    useEffect(() => {
        if (!orderId) { setLoading(false); return; }
        get(`${ServerEndpoint.GET_ORDER}/${orderId}`, true)
            .then(res => { if (res?.data?.data) setOrder(res.data.data); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [orderId]);

    const isPickup = order ? !order.includeDelivery : false;
    const activeSteps    = isPickup ? PICKUP_STEPS    : DELIVERY_STEPS;
    const statusRegistry = isPickup ? pickupRegistry  : deliveryRegistry;

    const currentStatusIndex = useMemo(() => {
        const idx = activeSteps.indexOf(order?.status || 'paid');
        return idx >= 0 ? idx : 0;
    }, [order?.status, activeSteps]);

    const progressPct = currentStatusIndex === 0 ? 0 : (currentStatusIndex / (activeSteps.length - 1)) * 100;
    const activeEntry = statusRegistry[currentStatusIndex];

    const courierInfo = useMemo(() => SystemDataService.courierInfo(links, order), [order, links]);

    return (
        <>
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-5 pt-10 pb-28 font-primary">
            <div className="w-full max-w-md flex flex-col gap-4">

                {/* ── Header card ── */}
                <motion.div
                    initial={{ y: -16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-primary-950 rounded-[2rem] p-8"
                >
                    <div className="h-8 mb-8 flex items-center justify-start p-8">
                        <MediaComponent media={logo?.image} className="h-full w-auto object-contain" useSekeleton={false} />
                    </div>
                    <h1 className="text-6xl font-black text-white uppercase  italic leading-none mb-4" style={{ fontFamily: 'Impact, sans-serif' }}>
                        {txt.tracking.trackerTitle}
                    </h1>
                    {orderId && (
                        <p className="text-xs font-medium text-white/40 mt-3">
                            {txt.header.registryId}: <span className="text-white/70 font-bold">{orderId}</span>
                        </p>
                    )}
                    {order?.email && (
                        <p className="text-xs font-medium text-white/30 mt-1">{order.email}</p>
                    )}

                    {/* Shipment method */}
                    {order && (
                        <div className="mt-5 pt-5 border-t border-white/10 flex flex-col items-center text-center gap-3">
                            {courierInfo ? (
                                <>
                                    <div className="h-16 w-28 bg-white rounded-2xl flex items-center justify-center p-3">
                                        {courierInfo.logoUrl
                                            ? <img src={courierInfo.logoUrl} alt={courierInfo.name} className="h-full w-full object-contain" />
                                            : <span className="text-sm font-black text-primary-950">{courierInfo.name}</span>
                                        }
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-0.5">Ships via</p>
                                        <p className="text-sm font-black text-white">{courierInfo.name}</p>
                                        {courierInfo.transitDays && (
                                            <p className="text-xs text-white/40 mt-0.5">{courierInfo.transitDays} business days</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="h-14 w-14 bg-white/10 rounded-2xl flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-0.5">Order Type</p>
                                        <p className="text-sm font-black text-white">Store Pickup</p>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* ── Status stepper ── */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-white rounded-[2rem] border border-gray-100 p-7"
                >
                    <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest mb-6">
                        {txt.progress.sectionLabel}
                    </p>

                    {loading ? (
                        <div className="flex flex-col gap-1">
                            {[0,1,2,3].map(i => (
                                <div key={i} className="flex items-center gap-5 py-4">
                                    <div className="w-12 h-12 rounded-2xl bg-gray-100 animate-pulse shrink-0" />
                                    <div className="h-3 bg-gray-100 rounded-full animate-pulse flex-1" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="relative flex flex-col">
                            {/* Rail */}
                            <div className="absolute left-[23px] top-6 bottom-6 w-0.5 bg-gray-100 rounded-full" />
                            {/* Animated fill */}
                            <motion.div
                                className="absolute left-[23px] top-6 w-0.5 bg-secondary-500 rounded-full"
                                initial={{ height: 0 }}
                                animate={{ height: `${progressPct}%` }}
                                transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            />

                            {statusRegistry.map((entry, i) => {
                                const isDone   = i <= currentStatusIndex;
                                const isActive = i === currentStatusIndex;
                                return (
                                    <div key={entry.key} className="flex items-center gap-5 py-4 relative">

                                        {/* Icon */}
                                        <div className="relative shrink-0">
                                            <motion.div
                                                initial={{ scale: 0.5, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                transition={{ delay: 0.2 + i * 0.08, type: 'spring', stiffness: 460, damping: 26 }}
                                                className={`relative w-12 h-12 rounded-2xl z-10 flex items-center justify-center p-2.5
                                                    ${isDone ? 'bg-primary-950' : 'bg-gray-50 border border-gray-100'}`}
                                            >
                                                {entry.asset?.image ? (
                                                    <MediaComponent
                                                        media={entry.asset.image}
                                                        useSekeleton={false}
                                                        className={`w-full h-full object-contain transition-all duration-500
                                                            ${isDone ? 'brightness-0 invert' : 'opacity-25'}`}
                                                    />
                                                ) : (
                                                    <span className={`text-sm font-black ${isDone ? 'text-white' : 'text-gray-300'}`}>{i + 1}</span>
                                                )}
                                            </motion.div>
                                        </div>

                                        {/* Label */}
                                        <div className="flex-1 min-w-0">
                                            <motion.p
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: isDone ? 1 : 0.3 }}
                                                transition={{ delay: 0.25 + i * 0.08 }}
                                                className={`text-base font-black leading-tight ${isDone ? 'text-primary-950' : 'text-gray-300'}`}
                                            >
                                                {entry.fallbackLabel}
                                            </motion.p>
                                            {isActive && (
                                                <motion.p
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.45 }}
                                                    className="text-xs text-secondary-500 font-bold mt-0.5"
                                                >
                                                    {txt.progress.current}
                                                </motion.p>
                                            )}
                                        </div>

                                        {/* Done badge */}
                                        {isDone && !isActive && (
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ delay: 0.4 + i * 0.08, type: 'spring', stiffness: 500, damping: 22 }}
                                                className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0"
                                            >
                                                <svg className="w-3 h-3 overflow-visible" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth={3.5}>
                                                    <motion.path
                                                        strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"
                                                        initial={{ pathLength: 0 }}
                                                        animate={{ pathLength: 1 }}
                                                        transition={{ delay: 0.5 + i * 0.08, duration: 0.3, ease: 'easeOut' }}
                                                    />
                                                </svg>
                                            </motion.div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </motion.div>

                {/* ── Items ── */}
                {order && (order.items || []).length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="bg-white rounded-[2rem] border border-gray-100 p-7"
                    >
                        <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest mb-5">
                            {txt.manifest.title}
                        </p>
                        <div className="space-y-2.5">
                            {(order.items || []).map((item, i) => (
                                <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50">
                                    <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-white border border-gray-100 p-1.5">
                                        <MediaComponent media={item.image} className="w-full h-full object-contain mix-blend-multiply" useSekeleton={false} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm text-primary-950 truncate">{item.name}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">× {item.quantity}</p>
                                    </div>
                                    <p className="font-black text-sm text-primary-950 italic shrink-0">
                                        ${(item.price * item.quantity).toFixed(2)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Pricing breakdown */}
                        <div className="mt-5 pt-4 border-t border-gray-50 space-y-2">
                            {(() => {
                                const subtotal = (order.items || []).reduce((s, it) => s + it.price * it.quantity, 0);
                                const shipping = courierInfo?.cost || 0;
                                return (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <p className="text-xs font-bold text-gray-400">Subtotal</p>
                                            <p className="text-xs font-bold text-gray-500">${subtotal.toFixed(2)}</p>
                                        </div>
                                        {shipping > 0 && (
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-1.5">
                                                    {courierInfo?.logoUrl && (
                                                        <img src={courierInfo.logoUrl} alt={courierInfo.name} className="h-3 w-auto object-contain" />
                                                    )}
                                                    <p className="text-xs font-bold text-gray-400">{courierInfo.name}</p>
                                                </div>
                                                <p className="text-xs font-bold text-gray-500">${Number(shipping).toFixed(2)}</p>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                                            <p className="text-xs font-bold text-gray-400">Total</p>
                                            <p className="text-2xl font-black text-primary-950 italic er">${order.total?.toFixed(2)}</p>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Tracking link */}
                        {order.trackingUrl && (
                            <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-4 flex items-center justify-between w-full px-5 py-3.5 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                            >
                                <span className="text-xs font-black text-primary-950">{txt.tracking.btn || 'Track Shipment'}</span>
                                <svg className="w-4 h-4 text-secondary-500 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        )}
                    </motion.div>
                )}
            </div>
        </div>

        {/* ── Fixed mobile tracking bar ── */}
        <AnimatePresence>
            {!loading && order && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ delay: 0.6, type: 'spring', stiffness: 340, damping: 36 }}
                    className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe"
                >
                    <div className="max-w-md mx-auto bg-primary-950 rounded-[1.75rem] px-5 py-4 flex items-center gap-4 shadow-2xl shadow-primary-950/40">

                        {/* Active status icon */}
                        <div className="relative shrink-0">
                            {/* Ambient glow under icon */}
                            <motion.div
                                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                                className="relative w-10 h-10 rounded-xl bg-secondary-500 flex items-center justify-center p-2"
                            >
                                {activeEntry?.asset?.image ? (
                                    <MediaComponent
                                        media={activeEntry.asset.image}
                                        useSekeleton={false}
                                        className="w-full h-full object-contain brightness-0 invert"
                                    />
                                ) : (
                                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                            </motion.div>
                        </div>

                        {/* Status text */}
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest leading-none mb-0.5">
                                {txt.progress.sectionLabel}
                            </p>
                            <p className="text-sm font-black text-white leading-tight truncate">
                                {activeEntry?.fallbackLabel}
                            </p>
                        </div>

                        {/* Track button */}
                        {order.trackingUrl ? (
                            <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 flex items-center gap-2 bg-white/10 hover:bg-white/20 active:bg-white/5 transition-colors rounded-xl px-4 py-2.5"
                            >
                                <span className="text-xs font-black text-white">{txt.tracking.btn || 'Track'}</span>
                                <svg className="w-3.5 h-3.5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        ) : (
                            <div className="shrink-0 flex items-center gap-1.5 bg-white/5 rounded-xl px-4 py-2.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 animate-pulse" />
                                <span className="text-xs font-bold text-white/50">Live</span>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
        </>
    );
};

export default OrderTracker;
